import classnames from 'classnames';
import React, {
  ChangeEvent,
  cloneElement,
  ReactElement,
  ReactNode,
} from 'react';

import { Icon } from '../icon/Icon';
import { TooltipProps } from '../tooltip/Tooltip';
import styles from './Checkbox.module.scss';

export type CheckboxVariant = 'primary' | 'critical' | 'disabled';

export interface CheckBoxProps {
  className?: string;
  id?: string;
  tooltip?: ReactElement<Omit<TooltipProps, 'children'>>;
  testId?: string;
  hasError?: boolean;
  errorText?: ReactNode;
  name: string;
  label: React.ReactNode;
  onChange?: (name: string, event: ChangeEvent<HTMLInputElement>) => void;
  checked?: boolean;
  locked?: boolean;
  partial?: boolean;
  variant?: CheckboxVariant;
  size?: 'medium' | 'small';
}

export const CheckBox = ({
  className,
  onChange,
  label,
  name,
  id,
  tooltip,
  checked = false,
  locked = false,
  partial = false,
  variant = 'primary',
  size = 'medium',
  testId = 'custom-checkbox',
  hasError,
  errorText,
  ...rest
}: CheckBoxProps) => {
  const Label = tooltip ? (
    cloneElement(
      tooltip,
      {
        placement: tooltip.props.placement ?? 'top-start',
      },
      <span
        className={classnames(
          styles.checkboxLabel,
          styles.checkboxLabelWithTooltip
        )}
        data-checkbox-label
      >
        {label}
      </span>
    )
  ) : (
    <span className={styles.checkboxLabel} data-checkbox-label>
      {label}
    </span>
  );

  const iconName = partial ? 'Minus' : 'Checkmark';

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange?.(name, event);

  return (
    <div
      data-testid={testId}
      className={classnames(styles.wrapper, className)}
      data-checkbox-wrapper
    >
      <label
        htmlFor={id}
        className={classnames(
          styles.checkbox,
          styles[variant],
          size === 'medium' ? styles.sizeMedium : styles.sizeSmall,
          { [styles.checked]: checked, [styles.locked]: locked }
        )}
        data-checkbox
      >
        <input
          className={styles.checkboxElement}
          id={id}
          data-testid={id}
          type="checkbox"
          checked={checked}
          aria-checked={checked}
          name={name}
          onChange={onInputChange}
          disabled={variant === 'disabled' || locked}
          readOnly
          {...rest}
        />
        {locked ? (
          <Icon className={styles.lockIcon} iconName="LockLight" size={20} />
        ) : (
          <div className={styles.checkmark} data-checkmark>
            <Icon
              className={styles.checkIcon}
              iconName={iconName}
              size={16}
              data-testid="check"
              data-check-icon
            />
          </div>
        )}
        {Label}
      </label>
      {hasError && errorText && (
        <p className={styles.errorText} data-testid="error-text">
          {errorText}
        </p>
      )}
    </div>
  );
};
